import { getMessagingInstance } from '../config/firebaseConfig.js';
import { getToken, onMessage } from 'firebase/messaging';
import { api } from './api.js';
import { ENDPOINTS } from '../config/endpoints.js';
import { tokenService } from './tokenService.js';

// Instância do messaging (será inicializada quando necessário)
let messagingInstance = null;

/**
 * Serviço de notificações push
 */
export const pushNotificationService = {
    /**
     * Obtém a instância do messaging
     */
    async getMessaging() {
        if (!messagingInstance) {
            messagingInstance = await getMessagingInstance();
        }
        return messagingInstance;
    },

    /**
     * Verifica se o navegador suporta notificações
     */
    async isSupported() {
        const messaging = await this.getMessaging();
        return 'Notification' in window && 'serviceWorker' in navigator && messaging !== null;
    },

    /**
     * Solicita permissão para notificações
     */
    async requestPermission() {
        try {
            const permission = await Notification.requestPermission();
            return permission === 'granted';
        } catch (error) {
            console.error('Erro ao solicitar permissão:', error);
            return false;
        }
    },

    /**
     * Obtém o token FCM
     */
    async getFCMToken() {
        try {
            const isSupported = await this.isSupported();
            if (!isSupported) {
                console.warn('Notificações push não são suportadas neste navegador');
                return null;
            }

            // Verifica se há permissão
            if (Notification.permission !== 'granted') {
                const hasPermission = await this.requestPermission();
                if (!hasPermission) {
                    console.warn('Permissão para notificações negada');
                    return null;
                }
            }

            // Registra o service worker
            const registration = await navigator.serviceWorker.register('/firebase-messaging-sw.js');
            console.log('Service Worker registrado:', registration);

            // Obtém a instância do messaging
            const messaging = await this.getMessaging();

            // Obtém o token FCM
            const currentToken = await getToken(messaging, {
                vapidKey: process.env.REACT_APP_VAPID_KEY,
                serviceWorkerRegistration: registration
            });

            if (currentToken) {
                console.log('Token FCM obtido:', currentToken);
                return currentToken;
            } else {
                console.warn('Não foi possível obter o token FCM');
                return null;
            }
        } catch (error) {
            console.error('Erro ao obter token FCM:', error);
            return null;
        }
    },

    /**
     * Registra o token no backend
     */
    async registerToken(fcmToken) {
        try {
            const userToken = tokenService.getUserToken();

            if (!userToken) {
                throw new Error('Usuário não autenticado');
            }

            const response = await api.post(ENDPOINTS.PUSH_REGISTER, {
                token: fcmToken
            });

            if (response.data.error) {
                throw new Error(response.data.response || 'Erro ao registrar token');
            }

            console.log('Token registrado com sucesso no backend');

            // Salva o token localmente para referência futura
            localStorage.setItem('fcm_token', fcmToken);

            return response.data;
        } catch (error) {
            console.error('Erro ao registrar token no backend:', error);
            throw error;
        }
    },

    /**
     * Remove o token do backend
     */
    async deleteToken(fcmToken) {
        try {
            const userToken = tokenService.getUserToken();

            if (!userToken) {
                throw new Error('Usuário não autenticado');
            }

            // Se não foi passado um token, tenta recuperar do localStorage
            const tokenToDelete = fcmToken || localStorage.getItem('fcm_token');

            if (!tokenToDelete) {
                console.warn('Nenhum token para deletar');
                return;
            }

            const response = await api.post(ENDPOINTS.PUSH_DELETE, {
                token: tokenToDelete
            });

            if (response.data.error) {
                throw new Error(response.data.response || 'Erro ao deletar token');
            }

            console.log('Token deletado com sucesso do backend');

            // Remove o token do localStorage
            localStorage.removeItem('fcm_token');

            return response.data;
        } catch (error) {
            console.error('Erro ao deletar token do backend:', error);
            throw error;
        }
    },

    /**
     * Inicializa as notificações push
     */
    async initialize() {
        try {
            const isSupported = await this.isSupported();
            if (!isSupported) {
                console.warn('Notificações push não são suportadas');
                return false;
            }

            // Obtém o token FCM
            const fcmToken = await this.getFCMToken();

            if (!fcmToken) {
                return false;
            }

            // Registra o token no backend
            await this.registerToken(fcmToken);

            // Configura o listener para mensagens em foreground
            await this.setupForegroundListener();

            return true;
        } catch (error) {
            console.error('Erro ao inicializar notificações push:', error);
            return false;
        }
    },

    /**
     * Configura o listener para receber mensagens quando o app está em foreground
     */
    async setupForegroundListener() {
        const messaging = await this.getMessaging();
        if (!messaging) {
            return;
        }

        onMessage(messaging, (payload) => {
            console.log('Mensagem recebida em foreground:', payload);
            console.log('Payload completo:', JSON.stringify(payload, null, 2));

            // Verifica se tem permissão
            console.log('Notification.permission:', Notification.permission);
            if (Notification.permission !== 'granted') {
                console.warn('Sem permissão para mostrar notificação');
                return;
            }

            // Extrai os dados da notificação
            const notificationData = payload.notification || payload.data || {};
            const title = notificationData.title || payload.data?.title || 'Nova Notificação';
            const body = notificationData.body || payload.data?.body || '';
            const icon = notificationData.icon || payload.data?.icon || '/favicon.png';

            console.log('Exibindo notificação:', { title, body, icon });

            // Mostra a notificação usando o Service Worker Registration
            // Isso garante que a notificação seja sempre exibida, mesmo em foreground
            try {
                navigator.serviceWorker.ready.then(registration => {
                    console.log('Service Worker ready:', registration);
                    console.log('Service Worker state:', registration.active?.state);
                    
                    return registration.showNotification(title, {
                        body: body,
                        icon: icon,
                        badge: '/favicon.png',
                        tag: payload.data?.tag || 'default',
                        requireInteraction: false,
                        vibrate: [200, 100, 200],
                        data: payload.data || {}
                    });
                }).then(() => {
                    console.log('✅ Notificação exibida com sucesso via Service Worker');
                }).catch(err => {
                    console.error('❌ Erro ao exibir notificação via Service Worker:', err);
                    console.error('Detalhes do erro:', err.message, err.stack);
                });
            } catch (error) {
                console.error('❌ Erro ao criar notificação:', error);
                console.error('Detalhes do erro:', error.message, error.stack);
            }

            // Você pode adicionar lógica customizada aqui
            // Por exemplo, atualizar o estado da aplicação
            if (window.handlePushNotification) {
                window.handlePushNotification(payload);
            }
        });
    },

    /**
     * Limpa as notificações (útil no logout)
     */
    async cleanup() {
        try {
            const fcmToken = localStorage.getItem('fcm_token');

            if (fcmToken) {
                await this.deleteToken(fcmToken);
            }
        } catch (error) {
            console.error('Erro ao limpar notificações:', error);
        }
    }
};
